import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage, useAuth } from '@/lib/context';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Wrench, Search, MapPin, Clock, DollarSign, Eye, Users,
  ArrowRight, Plus, Sparkles, Home, ChevronRight, Star, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AlltooovottPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [country, setCountry] = useState(searchParams.get('country') || '');
  const [availability, setAvailability] = useState('');

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('listing_type', 'subcontracting');
      if (category) params.append('category', category);
      if (country) params.append('country', country);
      if (search) params.append('skills', search);

      const res = await fetch(`${API_URL}/api/marketplace/listings?${params}`);
      const data = await res.json();
      setListings(data);
    } catch (err) {
      console.error('Failed to fetch alltöövõtt:', err);
    } finally {
      setLoading(false);
    }
  }, [category, country, search]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-violet-600 flex items-center gap-1">
            <Home className="w-4 h-4" /> {t('common.home')}
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <Link to="/marketplace" className="text-gray-500 hover:text-violet-600">
            Marketplace
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">{t('marketplace.alltooovott.title')}</span>
        </div>
      </div>

      {/* Hero — premium, white/neutral background, violet accent */}
      <section className="bg-white border-b border-slate-100" data-testid="alltooovott-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-100 text-xs font-semibold mb-4"
                   data-testid="alltooovott-eyebrow">
                <Sparkles className="w-3.5 h-3.5" />
                {t('marketplace.alltooovott.eyebrow')}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight"
                  data-testid="alltooovott-title">
                {t('marketplace.alltooovott.title')}
              </h1>
              <p className="mt-3 text-lg text-slate-600 leading-relaxed"
                 data-testid="alltooovott-subtitle">
                {t('marketplace.alltooovott.subtitle')}
              </p>
              {/* 3 trust chips */}
              <div className="mt-5 flex flex-wrap gap-2" data-testid="alltooovott-trust-chips">
                {[
                  ['verifiedProviders', Star],
                  ['fastMatching', Clock],
                  ['b2bAndLocal', Award],
                ].map(([k, IconCmp]) => (
                  <span key={k}
                        data-testid={`alltooovott-chip-${k}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700">
                    <IconCmp className="w-3.5 h-3.5 text-violet-600" />
                    {t(`marketplace.alltooovott.trust.${k}`)}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0" data-testid="alltooovott-hero-actions">
              <Button
                onClick={() => navigate(user ? '/marketplace/new?type=subcontracting'
                                              : '/provider/register?category=subcontracting')}
                className="bg-violet-600 hover:bg-violet-700 text-white font-medium"
                data-testid="post-alltooovott-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('marketplace.alltooovott.cta')}
              </Button>
              <Link to="/hanked">
                <Button variant="outline"
                        className="border-slate-300 text-slate-700 hover:bg-slate-50"
                        data-testid="alltooovott-browse-requests-btn">
                  <Eye className="w-4 h-4 mr-2" />
                  {t('marketplace.alltooovott.browseRequests')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Filter card — sits directly under hero, looks like one module */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 mb-8">
        <Card className="shadow-sm border-slate-200" data-testid="alltooovott-filter-card">
          <CardContent className="p-4 sm:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
              <div className="lg:col-span-2 relative">
                <label className="text-xs font-medium text-slate-700 mb-1 block">
                  {t('marketplace.alltooovott.filters.skills')}
                </label>
                <Search className="absolute left-3 top-[34px] h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('marketplace.alltooovott.filters.skills')}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && fetchListings()}
                  data-testid="alltooovott-search-input"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">
                  {t('marketplace.hanked.filters.category')}
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="alltooovott-cat-trigger">
                    <SelectValue placeholder={t('marketplace.hanked.filters.category')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    <SelectItem value="construction">Construction</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="welding">Welding</SelectItem>
                    <SelectItem value="it">IT &amp; Tech</SelectItem>
                    <SelectItem value="logistics">Logistics</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">
                  {t('marketplace.alltooovott.filters.location')}
                </label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid="alltooovott-loc-trigger">
                    <SelectValue placeholder={t('marketplace.alltooovott.filters.location')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    <SelectItem value="Estonia">Estonia</SelectItem>
                    <SelectItem value="Finland">Finland</SelectItem>
                    <SelectItem value="Sweden">Sweden</SelectItem>
                    <SelectItem value="Norway">Norway</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">
                  {t('marketplace.alltooovott.filters.availability')}
                </label>
                <Select value={availability} onValueChange={setAvailability}>
                  <SelectTrigger data-testid="alltooovott-avail-trigger">
                    <SelectValue placeholder={t('marketplace.alltooovott.filters.availability')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    <SelectItem value="immediate">Immediately</SelectItem>
                    <SelectItem value="within_week">Within a week</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={fetchListings}
                      data-testid="alltooovott-search-btn"
                      className="bg-violet-600 hover:bg-violet-700 text-white">
                <Search className="w-4 h-4 mr-2" />
                {t('common.search')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="text-center py-12" data-testid="alltooovott-loading">
            <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-slate-500">{t('common.loading')}</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200"
               data-testid="alltooovott-empty">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-violet-50 border border-violet-100 flex items-center justify-center">
              <Users className="w-8 h-8 text-violet-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900">
              {t('marketplace.alltooovott.empty.title')}
            </h3>
            <p className="text-slate-500 mt-2 max-w-md mx-auto">
              {t('marketplace.alltooovott.empty.subtitle')}
            </p>
            <Button
              className="mt-5 bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => navigate(user ? '/marketplace/new?type=subcontracting'
                                            : '/provider/register?category=subcontracting')}
              data-testid="alltooovott-empty-cta"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('marketplace.alltooovott.empty.cta')}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map((listing) => (
              <ProviderCard 
                key={listing.id} 
                listing={listing} 
                onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProviderCard = ({ listing, onClick }) => {
  return (
    <Card 
      className="hover:shadow-lg transition-shadow cursor-pointer h-full border-slate-200"
      onClick={onClick}
      data-testid={`provider-card-${listing.id}`}
    >
      <CardContent className="p-6 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center shrink-0">
            <Wrench className="w-6 h-6 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{listing.owner_name || 'Provider'}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {listing.city || listing.country}
            </p>
          </div>
          {listing.owner_verified && (
            <Badge className="bg-violet-50 text-violet-700 border border-violet-100 shrink-0">
              <Star className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          )}
        </div>

        {/* Title & Description */}
        <h4 className="font-medium text-slate-800 mb-2">{listing.title}</h4>
        <p className="text-sm text-slate-600 line-clamp-2 mb-4 flex-1">{listing.description}</p>

        {/* Skills */}
        {listing.offered_skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {listing.offered_skills.slice(0, 4).map((skill, i) => (
              <Badge key={i} variant="outline" className="text-xs bg-slate-50">
                {skill}
              </Badge>
            ))}
            {listing.offered_skills.length > 4 && (
              <Badge variant="outline" className="text-xs bg-slate-50">
                +{listing.offered_skills.length - 4}
              </Badge>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            {listing.experience_years && (
              <span className="flex items-center gap-1">
                <Award className="w-3 h-3" />
                {listing.experience_years}+ years
              </span>
            )}
            {listing.availability && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {listing.availability}
              </span>
            )}
          </div>
          
          {(listing.hourly_rate_min || listing.hourly_rate_max) && (
            <span className="text-sm font-medium text-violet-700">
              €{listing.hourly_rate_min || 0}+/hr
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AlltooovottPage;
